import React, { useEffect, useState } from 'react';
import AppIcon from '../../components/AppIcon';
import SentMessagesPanel from '../../components/messages/SentMessagesPanel';
import client from '../../api/client';
import { Bell, Send, Loader2, Save, CheckCircle2, Zap } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import InfoTip from '../../components/InfoTip';

const MERGE_HINTS = ['{firstName}', '{coupleNames}', '{weddingDate}', '{weddingTime}', '{venue}', '{siteUrl}', '{countdown}'];

export default function Reminders() {
  const [tpls, setTpls] = useState(null);
  const [milestones, setMilestones] = useState([]);
  const [status, setStatus] = useState({});
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(null);
  const [confirmKey, setConfirmKey] = useState(null);
  const [settings, setSettings] = useState(null);
  const [settingsSaving, setSettingsSaving] = useState(false);
  const { toast } = useToast();

  const load = async () => {
    try {
      const [t, s, st] = await Promise.all([
        client.get('/reminders/templates'),
        client.get('/reminders/status'),
        client.get('/sms-settings'),
      ]);
      setTpls(t.data.templates);
      setMilestones([...t.data.milestones, { key: 'rsvp_chase', label: 'RSVP chase (only sent to non-responders)' }]);
      setStatus(s.data);
      setSettings(st.data);
    } catch (e) { /* ignore */ }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      await client.put('/reminders/templates', tpls);
      toast({ title: 'Reminder templates saved' });
    } catch (e) { toast({ title: 'Save failed', description: e?.response?.data?.detail || e.message }); }
    setSaving(false);
  };

  const saveSettings = async (next) => {
    setSettingsSaving(true);
    try {
      const { data } = await client.put('/sms-settings', next);
      setSettings(data);
    } catch (e) { toast({ title: 'Save failed', description: e?.response?.data?.detail || e.message }); }
    setSettingsSaving(false);
  };

  const sendNow = async (milestone) => {
    setBusy(milestone);
    try {
      const { data } = await client.post('/reminders/send', { milestone, confirm: true });
      toast({ title: `Sent ${data.sent} · Skipped ${data.skipped}` });
      await load();
    } catch (e) {
      toast({ title: 'Send failed', description: e?.response?.data?.detail || e.message });
    } finally { setBusy(null); setConfirmKey(null); }
  };

  const dryRun = async (milestone) => {
    setBusy(milestone);
    try {
      const { data } = await client.post('/reminders/send', { milestone, dryRun: true });
      toast({ title: `Dry run: would send ${data.sent}, skip ${data.skipped}` });
    } catch (e) {
      toast({ title: 'Dry run failed', description: e?.response?.data?.detail || e.message });
    } finally { setBusy(null); }
  };

  if (!tpls) return <div className="p-8"><Loader2 className="w-5 h-5 animate-spin text-[#7B2C3B]" /></div>;

  return (
    <div data-testid="reminders-page" className="animate-float-up">
      <h1 className="font-serif text-4xl text-[#2A2A2A] mb-1 flex items-center gap-3"><AppIcon feature="reminders" size="lg" reveal testId="section-icon-reminders" />SMS reminders</h1>
      <p className="text-[#6A6A6A] mb-6">Funny nudges sent automatically at 2 weeks, 1 week, day-before and 2 hours pre-ceremony. Edit the copy — the scheduler does the rest.</p>

      <div className="soft-card rounded-2xl p-4 bg-[#F4E4DA] border border-[#EFE3D8] mb-6 text-sm text-[#3A3A3A]">
        The scheduler runs hourly. If your wedding date is set, we&apos;ll auto-send each countdown milestone <strong>only to guests who RSVP&apos;d Yes</strong> and have a phone number. Guests who said No or haven&apos;t replied never get countdown texts.
        You can also send any milestone manually below — the system skips guests who already received that milestone.
      </div>

      <SentMessagesPanel onSeen={() => window.dispatchEvent(new Event('message-alerts-seen'))} />

      {/* SMS settings */}
      {settings && (
        <div data-testid="sms-settings" className="soft-card rounded-2xl p-5 bg-white border border-[#EFE3D8] mb-6">
          <h3 className="font-serif text-xl text-[#2A2A2A] mb-3 inline-flex items-center gap-2">
            SMS settings
            <InfoTip testId="info-sms-settings" title="How &amp; when reminders go out">
              <p>Fine-tune the automatic reminder engine. Changes save as you edit.</p>
              <ul className="list-disc ml-4 space-y-1">
                <li><strong>Allowed send from / until</strong>: quiet hours in your timezone. No reminder will fire outside this window (default 9am–8pm).</li>
                <li><strong>RSVP chase every X days</strong>: how often to nudge guests who haven&apos;t RSVPed yet.</li>
                <li><strong>Max chase attempts</strong>: we&apos;ll stop chasing a specific guest after this many tries — no one likes to feel hounded.</li>
                <li><strong>Auto-stop chase when guest RSVPs</strong>: turn this on to instantly stop chasing anyone who replies Yes/No.</li>
                <li><strong>Text/Email guests when they RSVP</strong>: send a warm "we got it!" confirmation.</li>
                <li><strong>ShareMyPartyPics promo</strong>: adds a small block promoting our sister app for guests to share photos.</li>
                <li><strong>Yourweddingfriends promo</strong>: invites your engaged guests to try our planner too.</li>
              </ul>
            </InfoTip>
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <label className="block">
              <span className="block text-[10px] uppercase tracking-wider text-[#9A8A7E] mb-1">Allowed send from</span>
              <input data-testid="sms-quiet-start" type="time" value={settings.quietHoursStart}
                onChange={(e) => setSettings({ ...settings, quietHoursStart: e.target.value })}
                onBlur={() => saveSettings(settings)}
                className="w-full px-2 py-1.5 rounded border border-[#EFE3D8] text-sm" />
            </label>
            <label className="block">
              <span className="block text-[10px] uppercase tracking-wider text-[#9A8A7E] mb-1">Allowed send until</span>
              <input data-testid="sms-quiet-end" type="time" value={settings.quietHoursEnd}
                onChange={(e) => setSettings({ ...settings, quietHoursEnd: e.target.value })}
                onBlur={() => saveSettings(settings)}
                className="w-full px-2 py-1.5 rounded border border-[#EFE3D8] text-sm" />
            </label>
            <label className="block">
              <span className="block text-[10px] uppercase tracking-wider text-[#9A8A7E] mb-1">RSVP chase every (days)</span>
              <input data-testid="sms-chase-interval" type="number" min="1" max="30" value={settings.rsvpChaseIntervalDays}
                onChange={(e) => setSettings({ ...settings, rsvpChaseIntervalDays: parseInt(e.target.value) || 7 })}
                onBlur={() => saveSettings(settings)}
                className="w-full px-2 py-1.5 rounded border border-[#EFE3D8] text-sm" />
            </label>
            <label className="block">
              <span className="block text-[10px] uppercase tracking-wider text-[#9A8A7E] mb-1">Max chase attempts</span>
              <input data-testid="sms-chase-max" type="number" min="1" max="10" value={settings.rsvpChaseMaxAttempts}
                onChange={(e) => setSettings({ ...settings, rsvpChaseMaxAttempts: parseInt(e.target.value) || 3 })}
                onBlur={() => saveSettings(settings)}
                className="w-full px-2 py-1.5 rounded border border-[#EFE3D8] text-sm" />
            </label>
          </div>
          <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
            <label className="flex items-center gap-2">
              <input data-testid="sms-auto-stop" type="checkbox" checked={settings.autoStopOnRsvp}
                onChange={(e) => saveSettings({ ...settings, autoStopOnRsvp: e.target.checked })} />
              Auto-stop chase when guest RSVPs
            </label>
            <label className="flex items-center gap-2">
              <input data-testid="sms-confirm-sms" type="checkbox" checked={settings.confirmationSmsEnabled}
                onChange={(e) => saveSettings({ ...settings, confirmationSmsEnabled: e.target.checked })} />
              Text guests when they RSVP
            </label>
            <label className="flex items-center gap-2">
              <input data-testid="sms-confirm-email" type="checkbox" checked={settings.confirmationEmailEnabled}
                onChange={(e) => saveSettings({ ...settings, confirmationEmailEnabled: e.target.checked })} />
              Email guests when they RSVP
            </label>
            <label className="flex items-center gap-2 col-span-1 sm:col-span-3">
              <input data-testid="sms-smpp-toggle" type="checkbox" checked={settings.promoteSharemypartypics !== false}
                onChange={(e) => saveSettings({ ...settings, promoteSharemypartypics: e.target.checked })} />
              Include a small <span className="mx-1 px-1.5 py-0.5 rounded bg-[#F3E7F5] text-[#8A4EA0] text-xs">ShareMyPartyPics</span> promo in invitation + RSVP confirmation emails
            </label>
            <label className="flex items-center gap-2 col-span-1 sm:col-span-3">
              <input data-testid="sms-refer-toggle" type="checkbox" checked={settings.referGuestPromoEnabled !== false}
                onChange={(e) => saveSettings({ ...settings, referGuestPromoEnabled: e.target.checked })} />
              Invite guests to try <span className="mx-1 px-1.5 py-0.5 rounded bg-[#F4E4DA] text-[#7B2C3B] text-xs">Yourweddingfriends</span> in invitation + RSVP confirmation emails (grows the community)
            </label>
          </div>

          <div className="mt-4 pt-4 border-t border-[#EFE3D8]">
            <p className="text-xs uppercase tracking-wider text-[#9A8A7E] mb-2 inline-flex items-center gap-1.5">
              Reminder lead times (hours before the wedding)
              <InfoTip testId="info-lead-times" title="When each reminder fires">
                <p>Each SMS reminder goes out this many hours before your wedding starts. The scheduler runs hourly and fires each one within ±2 hours of the value you set.</p>
                <p><strong>Quick conversions</strong>:</p>
                <ul className="list-disc ml-4 space-y-1">
                  <li>2 weeks = 336 hours</li>
                  <li>1 week = 168 hours</li>
                  <li>3 days = 72 hours</li>
                  <li>1 day (day-before) = 24 hours</li>
                  <li>2 hours pre-ceremony = 2</li>
                </ul>
                <p>Set any of them to <code>0</code> to skip that milestone entirely.</p>
              </InfoTip>
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <LeadTime testId="lead-two-weeks" label="Two-week reminder" value={settings.leadTwoWeeksHours ?? 336} onChange={(v) => saveSettings({ ...settings, leadTwoWeeksHours: v })} />
              <LeadTime testId="lead-one-week" label="One-week reminder" value={settings.leadOneWeekHours ?? 168} onChange={(v) => saveSettings({ ...settings, leadOneWeekHours: v })} />
              <LeadTime testId="lead-day-before" label="Day-before reminder" value={settings.leadDayBeforeHours ?? 24} onChange={(v) => saveSettings({ ...settings, leadDayBeforeHours: v })} />
              <LeadTime testId="lead-two-hours" label="Two-hour reminder" value={settings.leadTwoHoursHours ?? 2} onChange={(v) => saveSettings({ ...settings, leadTwoHoursHours: v })} />
            </div>
            <p className="text-[11px] text-[#9A8A7E] mt-2">Tip: enter hours (14 days = 336, 1 week = 168, 3 days = 72, 1 day = 24). The scheduler fires ±2h of the value.</p>
          </div>

          {/* RSVP reminder mode + chain editor. The couple picks between
              the legacy single chase (above) and a multi-step chain.
              Either / or — never both, so guests can't get double-nudged. */}
          <div className="mt-5 pt-4 border-t border-[#EFE3D8]">
            <label className="section-eyebrow">RSVP reminder mode</label>
            <div className="flex items-center gap-2 flex-wrap mt-2">
              <button
                data-testid="mode-single"
                onClick={() => saveSettings({ ...settings, rsvpReminderMode: 'single' })}
                className={`text-xs px-3 py-1.5 rounded-full border ${(settings.rsvpReminderMode || 'single') === 'single' ? 'bg-[#7B2C3B] text-white border-[#7B2C3B]' : 'bg-white border-[#EFE3D8]'}`}
              >Single chase (above)</button>
              <button
                data-testid="mode-chain"
                onClick={() => saveSettings({ ...settings, rsvpReminderMode: 'chain' })}
                className={`text-xs px-3 py-1.5 rounded-full border ${settings.rsvpReminderMode === 'chain' ? 'bg-[#7B2C3B] text-white border-[#7B2C3B]' : 'bg-white border-[#EFE3D8]'}`}
              >Multi-reminder chain</button>
              <span className="text-[11px] text-[#9A8A7E]">Either / or — the other mode is skipped so guests don't get double-nudged.</span>
            </div>
            {settings.rsvpReminderMode === 'chain' && <RsvpChainEditor allowedFrom={settings.quietHoursStart} allowedUntil={settings.quietHoursEnd} />}
          </div>
          {settingsSaving && <p className="text-[11px] text-[#9A8A7E] mt-2">Saving…</p>}
        </div>
      )}

      <div className="space-y-4">
        {milestones.map((m) => {
          const st = status[m.key] || {};
          const sent = st.sent || 0;
          return (
            <div key={m.key} data-testid={`reminder-${m.key}`} className="soft-card rounded-2xl p-5 bg-white border border-[#EFE3D8]">
              <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                <div>
                  <p className="section-eyebrow">{m.label}</p>
                  <h3 className="font-serif text-xl text-[#2A2A2A]">SMS reminder</h3>
                  {sent > 0 && <p className="text-[11px] text-[#4A7A4A] mt-0.5 inline-flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Sent to {sent} guest{sent === 1 ? '' : 's'}</p>}
                </div>
                <div className="flex items-center gap-2">
                  <button data-testid={`reminder-dryrun-${m.key}`} onClick={() => dryRun(m.key)} disabled={busy === m.key} className="text-xs px-3 py-1.5 rounded-full border border-[#EFE3D8] text-[#3A3A3A] disabled:opacity-60">
                    Dry run
                  </button>
                  {confirmKey === m.key ? (
                    <>
                      <button data-testid={`reminder-cancel-${m.key}`} onClick={() => setConfirmKey(null)} className="text-xs px-3 py-1.5 rounded-full border border-[#EFE3D8]">Cancel</button>
                      <button data-testid={`reminder-confirm-${m.key}`} onClick={() => sendNow(m.key)} disabled={busy === m.key} className="btn-primary text-xs px-4 py-1.5 rounded-full inline-flex items-center gap-1 disabled:opacity-60">
                        {busy === m.key ? <Loader2 className="w-3 h-3 animate-spin" /> : <Zap className="w-3 h-3" />} Confirm send
                      </button>
                    </>
                  ) : (
                    <button data-testid={`reminder-send-${m.key}`} onClick={() => setConfirmKey(m.key)} className="btn-primary text-xs px-4 py-1.5 rounded-full inline-flex items-center gap-1">
                      <Send className="w-3 h-3" /> Send now
                    </button>
                  )}
                </div>
              </div>
              <textarea
                data-testid={`reminder-text-${m.key}`}
                value={tpls[m.key] || ''}
                onChange={(e) => setTpls((p) => ({ ...p, [m.key]: e.target.value }))}
                rows={3}
                className="w-full px-3 py-2 rounded-lg border border-[#EFE3D8] bg-white text-sm focus:outline-none focus:border-[#7B2C3B]"
              />
              <p className="text-[10px] text-[#9A8A7E] mt-1">Merge fields: {MERGE_HINTS.join(' · ')}</p>
            </div>
          );
        })}
      </div>

      <div className="mt-5 flex items-center justify-end">
        <button data-testid="reminders-save" onClick={save} disabled={saving} className="btn-primary inline-flex items-center gap-1.5 text-xs px-4 py-2 rounded-full disabled:opacity-60">
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
          Save all reminders
        </button>
      </div>
    </div>
  );
}

function LeadTime({ testId, label, value, onChange }) {
  // Local state so typing doesn't fire a PUT per keystroke. Save on blur.
  const [local, setLocal] = React.useState(value);
  React.useEffect(() => { setLocal(value); }, [value]);
  const commit = () => {
    const v = parseInt(local, 10);
    if (Number.isNaN(v)) { setLocal(value); return; }
    if (v !== value) onChange(v);
  };
  return (
    <label className="block">
      <span className="block text-[10px] uppercase tracking-wider text-[#9A8A7E] mb-1">{label}</span>
      <div className="flex items-center gap-1">
        <input
          data-testid={testId}
          type="number"
          min="0"
          max="8760"
          value={local}
          onChange={(e) => setLocal(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.currentTarget.blur(); } }}
          className="w-full px-2 py-1.5 rounded border border-[#EFE3D8] text-sm"
        />
        <span className="text-[11px] text-[#9A8A7E]">hrs</span>
      </div>
    </label>
  );
}


// Multi-step RSVP reminder chain editor. Each step has an anchor (wedding
// date / invitation sent / reply-by date), a days offset, and its own
// message. Master allowed-send window from the SMS-settings block above
// applies to every step. Either/or with the single-chase editor.
function RsvpChainEditor({ allowedFrom, allowedUntil }) {
  const [steps, setSteps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await client.get('/reminders/rsvp-chain');
        setSteps(data.steps || []);
      } finally { setLoading(false); }
    })();
  }, []);
  const persist = async (next) => {
    setSteps(next);
    setSaving(true);
    try {
      const { data } = await client.put('/reminders/rsvp-chain', { steps: next });
      setSteps(data.steps);
    } finally { setSaving(false); }
  };
  const add = () => persist([...steps, { anchor: 'reply_by', days: 7, message: 'Hi {firstName}! Just a friendly nudge — please RSVP: {rsvpLink}', enabled: true }]);
  const patch = (i, part) => persist(steps.map((s, idx) => idx === i ? { ...s, ...part } : s));
  const remove = (i) => persist(steps.filter((_, idx) => idx !== i));
  const ANCHORS = [
    { value: 'wedding',         label: 'days before the wedding' },
    { value: 'reply_by',        label: 'days before the reply-by date' },
    { value: 'invitation_sent', label: 'days after the invitation was sent' },
  ];
  if (loading) return <p className="text-xs text-[#9A8A7E] mt-3">Loading chain…</p>;
  return (
    <div className="mt-3 space-y-3" data-testid="rsvp-chain-editor">
      <p className="text-[11px] text-[#9A8A7E]">Each reminder fires on its own anchor + day count. Master allowed send window ({allowedFrom} → {allowedUntil}) applies to every step. Chain stops as soon as the guest RSVPs or the reply-by date passes.</p>
      {steps.length === 0 && <p className="text-xs text-[#6A6A6A] italic">No reminders in the chain yet. Add the first one below.</p>}
      {steps.map((s, i) => (
        <div key={s.id || i} data-testid={`chain-step-${i}`} className="p-3 rounded-2xl bg-[#FBF7F2] border border-[#EFE3D8] space-y-2">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[10px] uppercase tracking-wider text-[#7B2C3B] font-medium">Reminder #{i + 1}</span>
            <label className="inline-flex items-center gap-1 text-[11px] cursor-pointer ml-auto">
              <input data-testid={`chain-enabled-${i}`} type="checkbox" checked={s.enabled !== false} onChange={(e) => patch(i, { enabled: e.target.checked })} className="accent-[#7B2C3B]" />
              Enabled
            </label>
            <button data-testid={`chain-remove-${i}`} onClick={() => remove(i)} className="text-[11px] text-red-600 hover:underline">Remove</button>
          </div>
          <div className="flex items-center gap-2 flex-wrap text-sm">
            <input
              data-testid={`chain-days-${i}`}
              type="number" min="0" max="365"
              value={s.days ?? 0}
              onChange={(e) => patch(i, { days: parseInt(e.target.value) || 0 })}
              className="w-20 px-2 py-1.5 rounded border border-[#EFE3D8]"
            />
            <select
              data-testid={`chain-anchor-${i}`}
              value={s.anchor}
              onChange={(e) => patch(i, { anchor: e.target.value })}
              className="px-3 py-1.5 rounded-full border border-[#EFE3D8] text-sm bg-white"
            >
              {ANCHORS.map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}
            </select>
          </div>
          <textarea
            data-testid={`chain-msg-${i}`}
            rows={2}
            value={s.message || ''}
            onChange={(e) => patch(i, { message: e.target.value })}
            className="w-full px-3 py-2 rounded-lg border border-[#EFE3D8] text-sm bg-white"
          />
        </div>
      ))}
      <button data-testid="chain-add" onClick={add} className="text-xs px-3 py-1.5 rounded-full bg-[#7B2C3B] text-white">+ Add reminder</button>
      {saving && <span className="ml-2 text-[11px] text-[#9A8A7E]">Saving…</span>}
      <p className="text-[10px] text-[#9A8A7E]">Merge fields: <code>{'{firstName}'}</code>, <code>{'{coupleNames}'}</code>, <code>{'{weddingDate}'}</code>, <code>{'{venue}'}</code>, <code>{'{rsvpLink}'}</code>, <code>{'{siteUrl}'}</code></p>
    </div>
  );
}
